import React from 'react';
import { LegalPage } from '../components/legal/LegalPage';
import { Seo } from '../components/Seo';

/**
 * LACloud Privacy Policy. Content reflects the real architecture: file
 * transfers are LAN, device-to-device; the cloud service is only a registry
 * that helps a user's own devices find each other. File contents never reach
 * our servers.
 */
export const PrivacyPolicy: React.FC = () => (
  <>
    <Seo
      title="Privacy Policy — Local Area Cloud"
      description="How Local Area Cloud handles your data: files move directly between your own devices and never touch our servers. Read the full privacy policy."
      path="/privacy"
    />
  <LegalPage kicker="Legal" title="Privacy Policy" effectiveDate="July 8, 2026">
    <div className="l-note">
      <strong>The short version.</strong> LACloud moves your files directly between your own
      devices on your local network. We never see, store, or transmit the contents of your files.
      Our servers hold only what's needed to sign you in and help your devices find each other —
      your email, your device names, and basic connection details. We don't sell your data.
    </div>

    <p>
      This Privacy Policy explains how <strong>Chrono Travel LLC</strong> ("Chrono Travel," "we,"
      "us," or "our"), the operator of the LACloud service and the localareacloud.com website and
      applications (together, the "Service"), collects, uses, and protects information about you.
      By creating an account or using the Service, you agree to this Policy.
    </p>

    <ul className="l-facts">
      <li><span className="k">Data controller</span><span className="v">Chrono Travel LLC</span></li>
      <li><span className="k">Contact</span><span className="v"><a href="mailto:privacy@localareacloud.com">privacy@localareacloud.com</a></span></li>
      <li><span className="k">Website</span><span className="v">localareacloud.com</span></li>
    </ul>

    <h2>1. How LACloud works (and why it matters for your privacy)</h2>
    <p>
      LACloud is a local-network file-sharing and backup tool. Discovery and file transfers happen
      <strong> directly between your devices over your own local network</strong> — the file data
      does not pass through our servers. Our cloud service acts as a <strong>registry</strong>: it
      records which devices belong to your account and how to reach them on your network, so your
      devices can find one another. This design means the most sensitive data — the contents of your
      files — stays on your devices and network, not with us.
    </p>

    <h2>2. Information we collect</h2>

    <h3>Account information</h3>
    <ul>
      <li>Your <strong>email address</strong>.</li>
      <li>Your <strong>password</strong>, stored only as a salted cryptographic hash (we cannot read it).</li>
      <li>
        If you sign in with <strong>Google or Microsoft</strong>, we receive your name and email
        address from that provider to create or match your account. We do not receive your provider
        password.
      </li>
    </ul>

    <h3>Device registry information</h3>
    <p>So your own devices can discover and connect to each other, we store, per device:</p>
    <ul>
      <li>A device name and a device identifier;</li>
      <li>The platform / operating system (e.g., Windows, iOS, Android);</li>
      <li>A <strong>hardware fingerprint</strong> — a derived, non-reversible value used to recognize the same device across sign-ins;</li>
      <li>Local network connection details (such as the device's local IP address and port) and the time it was last seen online.</li>
      <li>The public IP address the device most recently connected to our service from — kept as a single most-recent value (not a history), used for troubleshooting connectivity and detecting abuse.</li>
    </ul>

    <h3>Subscription and billing information</h3>
    <p>
      LACloud offers a free tier and a paid "Pro" tier. If you purchase Pro, payments are processed
      by our payment provider, <strong>Stripe</strong>. Stripe handles your card details directly —
      we do not receive or store your full payment card numbers. We store your subscription status
      and related identifiers needed to manage your plan.
    </p>

    <h3>Technical and usage information</h3>
    <p>
      We collect limited technical data needed to operate and secure the Service, such as
      authentication events and server logs (which may include IP addresses and timestamps).
    </p>

    <h2>3. Information we do NOT collect</h2>
    <ul>
      <li>
        <strong>The contents of your files.</strong> File transfers occur device-to-device on your
        local network. We do not upload, store, scan, or have access to your files or their contents.
      </li>
      <li>
        <strong>Your private security keys.</strong> For features that use encryption (including
        planned VPN functionality), private keys are generated and kept on your device. Our servers
        never hold your private keys, and any secrets synced between your devices are end-to-end
        encrypted so that we cannot read them.
      </li>
      <li>We do not sell your personal information, and we do not use it for third-party advertising.</li>
    </ul>

    <h2>4. How we use information</h2>
    <ul>
      <li>To create and secure your account and authenticate you;</li>
      <li>To register your devices and enable them to discover and connect to each other;</li>
      <li>To provide, maintain, and improve the Service, including automatic backups between your devices;</li>
      <li>To process subscriptions and manage your plan;</li>
      <li>To detect, prevent, and respond to fraud, abuse, and security issues;</li>
      <li>To communicate with you about your account, updates, and support requests;</li>
      <li>To comply with legal obligations.</li>
    </ul>

    <h2>5. When we share information</h2>
    <p>We share information only in these limited circumstances:</p>
    <ul>
      <li>
        <strong>Service providers</strong> who help us run the Service under contract, including our
        hosting and infrastructure providers (Google Cloud and Hostinger), our payment processor
        (Stripe), and identity providers you choose to use (Google, Microsoft).
      </li>
      <li>
        <strong>Legal reasons</strong> — if required by law, legal process, or to protect the rights,
        safety, and security of our users, the public, or Chrono Travel LLC.
      </li>
      <li>
        <strong>Business transfers</strong> — in connection with a merger, acquisition, or sale of
        assets, subject to this Policy.
      </li>
    </ul>
    <p>We do not sell your personal information.</p>

    <h2>6. Data retention</h2>
    <p>
      We keep account and device registry information for as long as your account is active. When you
      delete your account, we delete or anonymize the associated personal information within a
      reasonable period, except where we must retain limited records to comply with legal obligations,
      resolve disputes, or enforce our agreements.
    </p>
    <p>
      Security and diagnostic logs — such as authentication events, error reports, and the IP addresses
      associated with them — are kept only as long as needed for security, abuse prevention, and
      troubleshooting, and are then deleted or aggregated.
    </p>

    <h2>7. Your rights and choices</h2>
    <p>Depending on where you live, you may have the right to:</p>
    <ul>
      <li>Access the personal information we hold about you;</li>
      <li>Correct inaccurate information;</li>
      <li>Delete your account and associated personal information;</li>
      <li>Export a copy of certain information;</li>
      <li>Object to or restrict certain processing.</li>
    </ul>
    <p>
      You can delete your account at any time from within the app or by contacting us at{' '}
      <a href="mailto:privacy@localareacloud.com">privacy@localareacloud.com</a>. We will not
      discriminate against you for exercising these rights. If you are in the European Economic Area,
      United Kingdom, or California, additional rights under the GDPR/UK GDPR and CCPA/CPRA may apply,
      and you may lodge a complaint with your local data protection authority.
    </p>

    <h2>8. Security</h2>
    <p>
      We use technical and organizational measures to protect your information, including password
      hashing, encrypted connections to our servers, device-scoped access tokens, and a design that
      keeps your file contents and private keys on your own devices. No method of transmission or
      storage is perfectly secure, but we work to protect your information and to limit what we hold
      in the first place.
    </p>

    <h2>9. Children's privacy</h2>
    <p>
      The Service is not directed to children under 13 (or the minimum age required in your
      jurisdiction), and we do not knowingly collect personal information from them. If you believe a
      child has provided us information, contact us and we will delete it.
    </p>

    <h2>10. International users</h2>
    <p>
      LACloud is operated from the United States. If you access the Service from outside the U.S., you
      understand that your information may be processed in the United States, where data protection
      laws may differ from those in your country.
    </p>

    <h2>11. Changes to this Policy</h2>
    <p>
      We may update this Policy from time to time. When we make material changes, we will update the
      "Effective" date above and, where appropriate, notify you. Your continued use of the Service
      after an update means you accept the revised Policy.
    </p>

    <h2>12. Contact us</h2>
    <p>
      Questions about this Policy or your information? Contact Chrono Travel LLC at{' '}
      <a href="mailto:privacy@localareacloud.com">privacy@localareacloud.com</a>.
    </p>
  </LegalPage>
  </>
);

export default PrivacyPolicy;
